import { useState } from 'react';
import { Button } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { useDebouncedCallback } from '../../../../hooks';
import Flex from '../../../generic/Flex';
import { InputBase } from '../../../generic/Forms';
import Icon, { icons } from '../../../generic/Icon';

import ItemizedRuleSuggestionList from './ItemizedRuleSuggestionList';
import type { Suggestion } from './types';

type ItemizedRuleListInputProps = {
  decoration: string;
  getSuggestions: (term: string) => Promise<Suggestion[]>;
  addRule: (id: string | null) => void;
  removeRule: (id?: string) => void;
};

const ItemizedRuleListInput: VoidFunctionComponent<ItemizedRuleListInputProps> = ({
  decoration,
  addRule,
  removeRule,
  getSuggestions,
}) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const updateSuggestionList = useDebouncedCallback(async (queryTerm: string) => {
    if (!queryTerm) {
      setSuggestions([]);

      return;
    }
    const fetchedSuggestions = await getSuggestions(queryTerm);

    setSuggestions(fetchedSuggestions || []);
  }, 300);

  // clear the suggestions list and the existing input when user clicks outside of suggestion box
  const onExternalClick = () => {
    setSuggestions([]);
    setInput('');
  };

  // some api call
  return (
    <Flex justify="space-between" align="center">
      {/* Note: this replaces the DecoratedInput which was only used here */}
      <InputBase
        label={`Enter a ${decoration}`}
        value={input}
        size="small"
        onChange={term => {
          setInput(term);
          updateSuggestionList(term);
        }}
      >
        {inputBaseProps => (
          <div className="fossa-form__text-input__wrapper">
            <div className="fossa-form__text-input__wrapper__prefix">
              {decoration}
            </div>
            <input {...inputBaseProps} />
          </div>
        )}
      </InputBase>
      <Button onClick={() => removeRule()}>
        <span><Icon icon={icons.minus} /></span>
      </Button>
      <ItemizedRuleSuggestionList
        onExternalClick={onExternalClick}
        selectRule={(id) => {
          // clear suggestion list
          setSuggestions([]);
          addRule(id);
        }}
        suggestions={suggestions}
      />
    </Flex>
  );
};

export default ItemizedRuleListInput;
